import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Input, Space, Tag, Popconfirm, message, Checkbox, Empty, Typography, Select,
} from 'antd';
import {
  PlusOutlined, EnvironmentOutlined, EditOutlined, DeleteOutlined, ShopOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

const API_BASE = process.env.REACT_APP_API_BASE || '';
const BRAND = 'var(--brand-primary, #222957)';
const BORDER = '#E5E7F5';
const HEADING = '#222A5A';

const CVS_CHAINS = [
  { label: '7-ELEVEN', value: 'UNIMARTC2C' },
  { label: '全家 FamilyMart', value: 'FAMIC2C' },
  { label: '萊爾富 Hi-Life', value: 'HILIFEC2C' },
  { label: 'OK mart', value: 'OKMARTC2C' },
];
const CHAIN_LABEL = Object.fromEntries(CVS_CHAINS.map((c) => [c.value, c.label]));

const getToken = () => {
  try { return localStorage.getItem('customer_token'); } catch { return null; }
};

const EMPTY_HOME = { label: '', recipient_name: '', recipient_phone: '', recipient_address: '', is_default: false };

// 會員收件資料通訊錄：宅配到府 + 超商取貨門市（門市經綠界電子地圖選取）。
// 依記憶提醒：storefront 避免 antd v6 Modal（會整頁卡），改用就地展開表單。
export default function AddressBook() {
  const [list, setList] = useState(null);       // null=載入中
  const [editing, setEditing] = useState(null); // null | 'new-home' | <id>（宅配編輯）
  const [form, setForm] = useState(EMPTY_HOME);
  const [saving, setSaving] = useState(false);
  const [chain, setChain] = useState('UNIMARTC2C');
  // 從綠界地圖選店回來後，待補收件人資訊的門市
  const [pendingCvs, setPendingCvs] = useState(null); // {cvs_subtype, cvs_store_id, cvs_store_name, cvs_store_address}
  const [cvsForm, setCvsForm] = useState({ label: '', recipient_name: '', recipient_phone: '', is_default: false });

  const load = useCallback(() => {
    const token = getToken();
    if (!token) { setList([]); return; }
    fetch(`${API_BASE}/api/customer/addresses`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setList(Array.isArray(d) ? d : []))
      .catch(() => setList([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  // 綠界電子地圖選店導回 /member?cvs=1&subType&storeId&storeName&storeAddr → 進入待補收件人狀態
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('cvs') === '1' && q.get('storeId')) {
      setPendingCvs({
        cvs_subtype: q.get('subType') || 'UNIMARTC2C',
        cvs_store_id: q.get('storeId') || '',
        cvs_store_name: q.get('storeName') || '',
        cvs_store_address: q.get('storeAddr') || '',
      });
      setCvsForm({ label: '', recipient_name: '', recipient_phone: '', is_default: false });
      ['cvs', 'subType', 'storeId', 'storeName', 'storeAddr'].forEach((k) => q.delete(k));
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, []);

  const openNewHome = () => { setForm(EMPTY_HOME); setEditing('new-home'); };
  const openEditHome = (a) => {
    setForm({
      label: a.label || '', recipient_name: a.recipient_name || '',
      recipient_phone: a.recipient_phone || '', recipient_address: a.recipient_address || '',
      is_default: !!a.is_default,
    });
    setEditing(a.id);
  };
  const cancelHome = () => { setEditing(null); setForm(EMPTY_HOME); };

  const pickStore = () => {
    // 導向綠界電子地圖，選完導回 /member
    window.location.href = `${API_BASE}/api/logistics/ecpay/map?sub_type=${chain}&back=member`;
  };

  const saveHome = async () => {
    const token = getToken();
    if (!token) return;
    if (!form.recipient_name.trim() || !form.recipient_phone.trim() || !form.recipient_address.trim()) {
      message.warning('請填寫收件人姓名、電話與地址'); return;
    }
    setSaving(true);
    try {
      const isNew = editing === 'new-home';
      const res = await fetch(`${API_BASE}/api/customer/addresses${isNew ? '' : `/${editing}`}`, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, logistics_type: 'home' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { message.error(data.detail || '儲存失敗'); return; }
      message.success('收件資料已儲存'); cancelHome(); load();
    } catch { message.error('儲存失敗，請稍後再試'); } finally { setSaving(false); }
  };

  const saveCvs = async () => {
    const token = getToken();
    if (!token || !pendingCvs) return;
    if (!cvsForm.recipient_name.trim() || !cvsForm.recipient_phone.trim()) {
      message.warning('請填寫取件人姓名與電話'); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/customer/addresses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ logistics_type: 'cvs', ...pendingCvs, ...cvsForm }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { message.error(data.detail || '儲存失敗'); return; }
      message.success('超商門市已儲存'); setPendingCvs(null); load();
    } catch { message.error('儲存失敗，請稍後再試'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_BASE}/api/customer/addresses/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { message.error('刪除失敗'); return; }
      message.success('已刪除'); load();
    } catch { message.error('刪除失敗，請稍後再試'); }
  };

  const setDefault = async (id) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_BASE}/api/customer/addresses/${id}/default`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { message.error('設定失敗'); return; }
      load();
    } catch { message.error('設定失敗，請稍後再試'); }
  };

  const homeEditor = (
    <div style={{ padding: 14, borderRadius: 12, border: `1px dashed ${BRAND}`, background: '#F8F9FC', marginBottom: 12 }}>
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Input placeholder="標籤（家 / 公司，可留空）" value={form.label} maxLength={20}
          onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
        <Input placeholder="收件人姓名" value={form.recipient_name}
          onChange={(e) => setForm((f) => ({ ...f, recipient_name: e.target.value }))} />
        <Input placeholder="聯絡電話" inputMode="tel" value={form.recipient_phone}
          onChange={(e) => setForm((f) => ({ ...f, recipient_phone: e.target.value }))} />
        <Input.TextArea placeholder="完整收件地址（含縣市、郵遞區號）" value={form.recipient_address}
          autoSize={{ minRows: 2, maxRows: 4 }}
          onChange={(e) => setForm((f) => ({ ...f, recipient_address: e.target.value }))} />
        <Checkbox checked={form.is_default}
          onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}>設為預設宅配地址</Checkbox>
        <Space>
          <Button type="primary" loading={saving} onClick={saveHome}>儲存</Button>
          <Button onClick={cancelHome}>取消</Button>
        </Space>
      </Space>
    </div>
  );

  const cvsEditor = pendingCvs && (
    <div style={{ padding: 14, borderRadius: 12, border: `1px dashed ${BRAND}`, background: '#f6ffed', marginBottom: 12 }}>
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <div>
          <Tag color="green">{CHAIN_LABEL[pendingCvs.cvs_subtype] || '超商'}</Tag>
          <Text strong>{pendingCvs.cvs_store_name}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{pendingCvs.cvs_store_address}</Text></div>
        </div>
        <Input placeholder="標籤（可留空）" value={cvsForm.label} maxLength={20}
          onChange={(e) => setCvsForm((f) => ({ ...f, label: e.target.value }))} />
        <Input placeholder="取件人姓名" value={cvsForm.recipient_name}
          onChange={(e) => setCvsForm((f) => ({ ...f, recipient_name: e.target.value }))} />
        <Input placeholder="取件人電話" inputMode="tel" value={cvsForm.recipient_phone}
          onChange={(e) => setCvsForm((f) => ({ ...f, recipient_phone: e.target.value }))} />
        <Checkbox checked={cvsForm.is_default}
          onChange={(e) => setCvsForm((f) => ({ ...f, is_default: e.target.checked }))}>設為預設超商門市</Checkbox>
        <Space>
          <Button type="primary" loading={saving} onClick={saveCvs}>儲存門市</Button>
          <Button onClick={() => setPendingCvs(null)}>取消</Button>
        </Space>
      </Space>
    </div>
  );

  const renderCard = (a) => {
    if (editing === a.id) return <div key={a.id}>{homeEditor}</div>;
    const isCvs = a.logistics_type === 'cvs';
    return (
      <div key={a.id} style={{
        padding: '12px 14px', borderRadius: 12, border: `1px solid ${a.is_default ? BRAND : '#eee'}`,
        background: a.is_default ? '#F8F9FC' : '#fff',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <Space size={6} wrap>
              <Tag color={isCvs ? 'green' : 'blue'}>{isCvs ? '超商' : '宅配'}</Tag>
              <Text strong style={{ color: HEADING }}>{a.recipient_name}</Text>
              {a.label ? <Tag>{a.label}</Tag> : null}
              {a.is_default ? <Tag color="geekblue">預設</Tag> : null}
            </Space>
            <div><Text type="secondary" style={{ fontSize: 13 }}>{a.recipient_phone}</Text></div>
            {isCvs ? (
              <div><Text type="secondary" style={{ fontSize: 13 }}>
                {CHAIN_LABEL[a.cvs_subtype] || '超商'}・{a.cvs_store_name}（{a.cvs_store_address}）
              </Text></div>
            ) : (
              <div><Text type="secondary" style={{ fontSize: 13 }}>{a.recipient_address}</Text></div>
            )}
          </div>
          <Space direction="vertical" align="end" size={2}>
            {!isCvs && <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditHome(a)}>編輯</Button>}
            <Popconfirm title="刪除這筆收件資料？" okText="刪除" cancelText="取消" onConfirm={() => remove(a.id)}>
              <Button type="text" danger size="small" icon={<DeleteOutlined />}>刪除</Button>
            </Popconfirm>
            {!a.is_default && <Button type="link" size="small" onClick={() => setDefault(a.id)}>設為預設</Button>}
          </Space>
        </div>
      </div>
    );
  };

  return (
    <Card
      title={<span style={{ fontWeight: 800, color: BRAND }}><EnvironmentOutlined /> 收件資料</span>}
      style={{ borderRadius: 16, borderColor: BORDER }}
      styles={{ body: { padding: 16 } }}
    >
      {/* 新增動作 */}
      <Space wrap style={{ marginBottom: 12 }}>
        {editing == null && <Button icon={<PlusOutlined />} onClick={openNewHome}>新增宅配地址</Button>}
        <Space.Compact>
          <Select size="middle" value={chain} onChange={setChain} options={CVS_CHAINS} style={{ width: 150 }} />
          <Button icon={<ShopOutlined />} onClick={pickStore}>選擇超商門市</Button>
        </Space.Compact>
      </Space>

      {editing === 'new-home' && homeEditor}
      {cvsEditor}

      {list === null ? (
        <Text type="secondary">載入中…</Text>
      ) : list.length === 0 && editing == null && !pendingCvs ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未新增收件資料，結帳時可自動帶入" style={{ margin: '8px 0' }} />
      ) : (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {list.map(renderCard)}
        </Space>
      )}
    </Card>
  );
}
